/**
 * The mode switcher in the top bar - swap hats without signing out.
 *
 * Stepping DOWN to Employee is one click. Stepping UP asks HR Manager, HR Admin,
 * IT INF and Super Admin for their password (1c), so a machine left open in
 * Employee mode cannot be escalated by whoever walks past it.
 *
 * Not rendered for a plain employee, or for an account with no employee record -
 * both have only one way in.
 */
import React, { useState } from 'react';
import { Briefcase, Check, ChevronDown, Loader2, Lock, ShieldCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { roleLabel } from '@/components/auth/ModeChooserDialog';

export const ModeSwitcher = () => {
  const {
    mode, actualRole, employeeModeAvailable, modeStepUpRequired, selectMode,
  } = useAuth();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!employeeModeAvailable || !mode) return null;

  const label = roleLabel(actualRole, isRTL);
  const inEmployeeMode = mode === 'employee';

  const switchTo = async (target) => {
    if (target === mode) return;
    if (target === 'role' && modeStepUpRequired) {
      setError(null);
      setPassword('');
      setStepUpOpen(true);
      return;
    }
    setBusy(true);
    await selectMode(target);
    setBusy(false);
  };

  const confirmStepUp = async () => {
    setBusy(true);
    const result = await selectMode('role', { password });
    setBusy(false);
    if (result.success) {
      setStepUpOpen(false);
      setPassword('');
    } else {
      setError(result.error);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50"
            data-testid="mode-switcher-trigger"
          >
            {inEmployeeMode
              ? <Briefcase className="h-3.5 w-3.5 text-slate-500" />
              : <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}
            <span
              className="truncate max-w-[88px] sm:max-w-none"
              data-testid="mode-switcher-current"
            >
              {inEmployeeMode ? (isRTL ? 'موظف' : 'Employee') : label}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-64">
          <DropdownMenuLabel className="text-xs font-normal text-slate-500">
            {isRTL ? 'أنت تعمل الآن كـ' : 'You are working as'}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => switchTo('employee')}
            className="cursor-pointer gap-2"
            data-testid="mode-switch-to-employee"
          >
            <Briefcase className="h-4 w-4 text-slate-500" />
            <span className="flex-1">{isRTL ? 'موظف' : 'Employee'}</span>
            {inEmployeeMode && <Check className="h-4 w-4 text-emerald-600" />}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => switchTo('role')}
            className="cursor-pointer gap-2"
            data-testid="mode-switch-to-role"
          >
            <ShieldCheck className="h-4 w-4 text-slate-500" />
            <span className="flex-1">{label}</span>
            {!inEmployeeMode
              ? <Check className="h-4 w-4 text-emerald-600" />
              : modeStepUpRequired && <Lock className="h-3.5 w-3.5 text-slate-400" />}
          </DropdownMenuItem>
          {busy && (
            <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" />
              {isRTL ? 'جاري التبديل...' : 'Switching...'}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={stepUpOpen} onOpenChange={setStepUpOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mode-step-up-dialog">
          <DialogHeader>
            <DialogTitle>{isRTL ? `المتابعة كـ ${label}` : `Continue as ${label}`}</DialogTitle>
            <DialogDescription>
              {isRTL
                ? 'أدخل كلمة المرور لتأكيد هويتك قبل استخدام صلاحيات دورك.'
                : 'Enter your password to confirm it is you before taking up your role\u2019s authority.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="step-up-password">{isRTL ? 'كلمة المرور' : 'Password'}</Label>
            <Input
              id="step-up-password"
              type="password"
              value={password}
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && password) confirmStepUp(); }}
              data-testid="mode-step-up-password"
            />
            {error && (
              <p className="text-sm text-red-600" data-testid="mode-step-up-error">{error}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStepUpOpen(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={confirmStepUp}
              disabled={!password || busy}
              data-testid="mode-step-up-submit"
            >
              {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {isRTL ? 'متابعة' : 'Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModeSwitcher;
